/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ContentGrid/ContentGrid.css';

export interface ContentGridProps extends HTMLAttributes<HTMLDivElement> {
  emptyGridContent: VNode;
  maxColumns?: number;
  columnWidth?: string;
}

export const ContentGrid: FunctionComponent<ContentGridProps> = ({
  className,
  children,
  maxColumns,
  columnWidth = '1fr',
  emptyGridContent,
  ...props
}) => {
  const hasChildren =
    !!children && (Array.isArray(children) ? children.length > 0 : true);

  const additionalStyles = hasChildren
    ? {
        gridTemplateColumns: `repeat(${maxColumns}, ${columnWidth})`,
      }
    : undefined;

  return (
    <div
      {...props}
      className={classes(['dropin-content-grid', className])}
      tabindex={0}
    >
      <div
        data-testid="content-grid-content"
        className={classes([
          'dropin-content-grid__content',
          ['dropin-content-grid__dynamic-columns-content', !maxColumns],
          ['dropin-content-grid__content--empty', !hasChildren],
        ])}
        style={additionalStyles}
      >
        {hasChildren ? children : emptyGridContent}
      </div>
    </div>
  );
};
